import { DealerOwnerSelect } from '../DealerOwnerSelect';
import { UNKNOWN_DEALER_LABEL } from '../../utils/templateOwnership';
import { DOCUMENT_TYPE_LABELS } from '../../utils/documentTypeLabels';
import { useProductDocumentsPanel } from '../../hooks/useProductDocumentsPanel';
import type { AssignTarget } from '../../hooks/useAssignTemplateForm';
import { AssignTemplateModal } from './AssignTemplateModal';
import { ProductDocumentsView } from './ProductDocumentsView';
import { UnassignTemplateConfirm } from './UnassignTemplateConfirm';

interface Props {
  productId: string;
}

function contextLinesFor(target: AssignTarget | null, dealerNames: ReadonlyMap<string, string>) {
  if (target?.kind === 'product-slot') {
    return [
      { label: 'Document', value: DOCUMENT_TYPE_LABELS[target.documentType] },
      { label: 'Applies to', value: 'All dealers' },
    ];
  }
  if (target?.kind === 'change') {
    const { dealerId, documentType } = target.assignment;
    return [
      { label: 'Document', value: DOCUMENT_TYPE_LABELS[documentType] },
      { label: 'Applies to', value: dealerId ? (dealerNames.get(dealerId) ?? UNKNOWN_DEALER_LABEL) : 'All dealers' },
    ];
  }
  return [];
}

export function ProductDocumentsPanel({ productId }: Props) {
  const panel = useProductDocumentsPanel(productId);
  const { form, target } = panel.modal;

  return (
    <>
      <ProductDocumentsView
        status={panel.status}
        errorMessage={panel.errorMessage}
        truncationNotice={panel.truncationNotice}
        slots={panel.slots}
        dealerAssignments={panel.dealerAssignments}
        dealerNames={panel.dealerNames}
        isResolvingDealers={panel.isResolvingDealers}
        {...panel.abilities}
        {...panel.callbacks}
      />
      <AssignTemplateModal
        isOpen={panel.modal.isOpen}
        mode={form.mode}
        values={form.values}
        errors={form.errors}
        formError={form.formError}
        contextLines={contextLinesFor(target, panel.dealerNames)}
        templateOptions={panel.modal.templateOptions}
        templatesStatus={panel.modal.templatesStatus}
        templatesNotice={panel.modal.templatesNotice}
        dealerPicker={
          form.mode === 'dealer-override' ? (
            <DealerOwnerSelect
              id="assign-dealer"
              value={form.values.dealerId || null}
              onChange={(dealerId) => form.setValues({ dealerId: dealerId ?? '' })}
              placeholder="Search dealers by name or code..."
            />
          ) : undefined
        }
        isSaving={form.isSaving}
        onValuesChange={form.setValues}
        onSubmit={form.submit}
        onClose={panel.modal.close}
      />
      <UnassignTemplateConfirm
        isOpen={panel.unassign.isOpen}
        documentLabel={panel.unassign.documentLabel}
        dealerName={panel.unassign.dealerName}
        isPending={panel.unassign.isPending}
        errorMessage={panel.unassign.errorMessage}
        onConfirm={panel.unassign.confirm}
        onClose={panel.unassign.close}
      />
    </>
  );
}
